import React, { useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputAdornment, TextField } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';

import { useLibrary, useLibraryAlert } from "../../LibraryPage";
import { LinkAssetService, SaveLinkAssetRequest } from "../../../../services/LinkAssetService";
import { AssetCategory } from "../../../../entities/AssetCategory";
import { useIsMobile } from "../../../../components/Mui3WidthProvider";
import { FieldValidationError, ValidationHelper } from "../../../../services/server/ServerResponses";
import { convertLinkAssetToLibraryItem } from "../../../../entities/LinkAsset";

interface Props{
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const youtubeRegex = new RegExp(/(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/, "i")

export function YoutubeLinkDialog(props:Props){
    const libraryAlert = useLibraryAlert();
    const context = useLibrary();
    const { activeFolderId } = context;
    const isMobile = useIsMobile();

    const [title, setTitle] = useState("");
    const [url, setUrl] = useState("");
    const [disabled, setDisabled] = useState(false);
    const [embeddedLinkId, setEmbeddedLinkId] = useState<string>();
    const [errors, setErrors] = useState<FieldValidationError[]>([]);

    const videoHeight = useMemo(() => isMobile ? 'auto' : '315', [isMobile]);
    const videoWidth = useMemo(() => isMobile ? 'auto' : '550', [isMobile]);

    const tryParseUrl = (e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrors([]);
        const parsed = url.match(youtubeRegex);

        if (parsed && parsed.length > 0)
            return setEmbeddedLinkId(parsed[1]);

        const serverError: FieldValidationError = { field: "Url", errors: ['Sorry, this YouTube url is invalid. (Example: https://youtu.be/ELIqj32_IJs)'] };
        setErrors([serverError]);
    }

    const onSave = async(e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        ///TODO: is all this needed
        const request: SaveLinkAssetRequest = {
            parentId: activeFolderId ?? undefined,
            name: title,
            isStarred: false,
            dateAccessed: new Date(),
            category: AssetCategory.Video,
            url: `youtube://${embeddedLinkId}`,
            locationPreached: "",
            scripture: "",
        };

        setDisabled(true);
        const response = await LinkAssetService.save(request);
        setDisabled(false);

        if(response.validation) return setErrors(response.errors);
        if(response.error) return libraryAlert(response.message);

        context.addLibraryItem(convertLinkAssetToLibraryItem(response.data));
        libraryAlert(`${title} added to library`);
        return props.onSuccess();
    }

    return (
        <Dialog 
            open={props.open} 
            onClose={props.onClose} 
            fullWidth
            fullScreen={isMobile}
            onKeyDown={(e) => e.key === "Tab" ? e.stopPropagation() : undefined}
        >
            <DialogTitle id="form-dialog-title">New Linked YouTube Video
                {isMobile && <CloseIcon style={{ float: "right", color: "rgb(0, 0, 0, .4)"}} onClick={props.onClose} />}
            </DialogTitle>
            <form onSubmit={embeddedLinkId ? onSave : tryParseUrl}>
                <DialogContent style={{ paddingTop: 0 }}>
                    <DialogContentText>
                        Add a link to your YouTube video.
                    </DialogContentText>
                    <TextField
                        required
                        autoFocus={true}
                        disabled={disabled}
                        margin="dense"
                        id="newItemName"
                        label="Name"
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.currentTarget.value)}
                        fullWidth
                        error={ValidationHelper.isFieldInError(errors, 'Name')}
                        helperText={<span dangerouslySetInnerHTML={{ __html: ValidationHelper.getFieldErrorSummary(errors, 'Name') }} />}
                    />
                    <TextField
                        type="text"
                        disabled={embeddedLinkId !== undefined || disabled}
                        label="Video URL"
                        margin="dense"
                        fullWidth
                        required
                        value={url}
                        onChange={(e) => setUrl(e.currentTarget.value)}
                        helperText={'Example: https://youtu.be/ELIqj32_IJs'}
                        error={ValidationHelper.isFieldInError(errors, 'Url')}
                        InputProps={{
                            endAdornment:
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="remove url"
                                        onClick={() => { setEmbeddedLinkId(undefined); setUrl("")}}
                                        style={{display: embeddedLinkId ? "block" : "none"}}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                </InputAdornment>
                        }}

                    />
                    {embeddedLinkId
                        &&
                        <>
                            <br />
                            <br />
                            <iframe title="YouTube" width={videoWidth} height={videoHeight} frameBorder={0} allowFullScreen src={`https://www.youtube-nocookie.com/embed/${embeddedLinkId}`} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" />
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Button color="primary" onClick={props.onClose}>
                        Cancel
                    </Button>
                    <Button color="primary" disabled={url.length === 0 || disabled} type="submit">
                        { embeddedLinkId ? "Add" : "Validate" }
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}